import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import {
  CommonActivity,
  ActivityGroup,
  ActivityChild,
  AdhocActivity
} from '../../models/adhoc-service.model';

export interface ActivitySelectDialogData {
  commonActivities: CommonActivity[];
  activityGroups: ActivityGroup[];
  adhocCommonActivities: AdhocActivity[];
  clientTopActivities: AdhocActivity[];
  activityDurationMap: Map<string, number>;
}

export interface ActivitySelectResult {
  activity_id: string;
  description: string;
  roles: string[];
  duration: number | null;
}

@Component({
  selector: 'app-activity-select-dialog',
  templateUrl: './activity-select-dialog.component.html',
  styleUrls: ['./activity-select-dialog.component.scss']
})
export class ActivitySelectDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  searchControl = new FormControl<string>('');
  viewMode: 'sah' | 'all' = 'sah';
  selectedActivity: ActivitySelectResult | null = null;
  selectedActivityGroup: ActivityGroup | null = null;
  filteredActivities: Array<{ id: string; description: string; parentDescription: string; roles: string[] }> = [];

  private lastClickTime = 0;
  private lastClickedId: string | null = null;
  private readonly DOUBLE_CLICK_DELAY = 400;

  constructor(
    public dialogRef: MatDialogRef<ActivitySelectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ActivitySelectDialogData
  ) {}

  sortedActivityGroups: ActivityGroup[] = [];

  ngOnInit(): void {
    this.sortedActivityGroups = [...this.data.activityGroups]
      .sort((a, b) => a.description.localeCompare(b.description, undefined, { sensitivity: 'base' }));

    this.searchControl.valueChanges.pipe(
      debounceTime(150),
      takeUntil(this.destroy$)
    ).subscribe(() => this.updateFilteredActivities());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get searchTerm(): string {
    return (this.searchControl.value || '').toLowerCase().trim();
  }

  private matchesTerm(term: string, ...texts: string[]): boolean {
    if (!term) return true;
    return texts.some(t => t && t.toLowerCase().includes(term));
  }

  private updateFilteredActivities(): void {
    const term = this.searchTerm;
    if (!term) {
      this.filteredActivities = [];
      return;
    }

    const seen = new Set<string>();
    const results: Array<{ id: string; description: string; parentDescription: string; roles: string[] }> = [];

    if (this.viewMode === 'sah') {
      for (const activity of this.getSahActivities()) {
        if (this.matchesTerm(term, activity.description, activity.parent_description)) {
          if (!seen.has(activity.activity_detail_id)) {
            seen.add(activity.activity_detail_id);
            results.push({
              id: activity.activity_detail_id,
              description: activity.description,
              parentDescription: activity.parent_description || '',
              roles: activity.rs_role_ids || []
            });
          }
        }
      }
    } else {
      for (const group of this.sortedActivityGroups) {
        for (const child of group.activities) {
          if (this.matchesTerm(term, child.description, group.description)) {
            if (!seen.has(child.id)) {
              seen.add(child.id);
              results.push({
                id: child.id,
                description: child.description,
                parentDescription: group.description,
                roles: child.roles || []
              });
            }
          }
        }
      }
    }

    this.filteredActivities = results.sort((a, b) =>
      a.description.localeCompare(b.description, undefined, { sensitivity: 'base' })
    );
  }

  getFilteredClientTopActivities(): AdhocActivity[] {
    const term = this.searchTerm;
    if (!term) return this.data.clientTopActivities;
    return this.data.clientTopActivities.filter(a => this.matchesTerm(term, a.activity_name));
  }

  getFilteredTopAdhocActivities(): AdhocActivity[] {
    const clientIds = new Set(this.data.clientTopActivities.map(a => a.activity_id));
    const deduped = this.data.adhocCommonActivities
      .filter(a => !clientIds.has(a.activity_id))
      .slice(0, 6);
    const term = this.searchTerm;
    if (!term) return deduped;
    return deduped.filter(a => this.matchesTerm(term, a.activity_name));
  }

  private toResult(id: string, description: string, roles: string[]): ActivitySelectResult {
    return {
      activity_id: id,
      description,
      roles,
      duration: this.data.activityDurationMap.get(id) ?? null
    };
  }

  getSahActivities(): CommonActivity[] {
    return this.data.commonActivities
      .filter(a => a.parent_description && a.parent_description.toLowerCase().includes('s@h'))
      .sort((a, b) => a.description.localeCompare(b.description, undefined, { sensitivity: 'base' }));
  }

  getTopAdhocActivities(): AdhocActivity[] {
    return this.data.adhocCommonActivities.slice(0, 6);
  }

  // Activity group two-step picker
  selectActivityGroup(group: ActivityGroup): void {
    this.selectedActivityGroup = group;
  }

  clearActivityGroup(): void {
    this.selectedActivityGroup = null;
  }

  // Click handlers — normalize to ActivitySelectResult
  onAdhocActivityClick(activity: AdhocActivity): void {
    const result = this.toResult(activity.activity_id, activity.activity_name, []);
    this.handleClick(result);
  }

  onCommonActivityClick(activity: CommonActivity): void {
    const result = this.toResult(activity.activity_detail_id, activity.description, activity.rs_role_ids || []);
    this.handleClick(result);
  }

  onChildActivityClick(activity: ActivityChild): void {
    const result = this.toResult(activity.id, activity.description, activity.roles || []);
    this.handleClick(result);
  }

  onSearchResultClick(activity: { id: string; description: string; parentDescription: string; roles: string[] }): void {
    const result = this.toResult(activity.id, activity.description, activity.roles);
    this.handleClick(result);
  }

  private handleClick(result: ActivitySelectResult): void {
    const now = Date.now();
    const isDoubleClick = (now - this.lastClickTime) < this.DOUBLE_CLICK_DELAY
      && this.lastClickedId === result.activity_id;
    this.lastClickTime = now;
    this.lastClickedId = result.activity_id;

    if (isDoubleClick) {
      this.dialogRef.close(result);
      return;
    }

    this.selectedActivity = result;
  }

  onSelect(): void {
    if (this.selectedActivity) {
      this.dialogRef.close(this.selectedActivity);
    }
  }

  isSelected(id: string): boolean {
    return this.selectedActivity?.activity_id === id;
  }

  setViewMode(mode: 'sah' | 'all'): void {
    this.viewMode = mode;
    this.selectedActivityGroup = null;
    this.updateFilteredActivities();
  }
}
